import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  ICampaign,
  CampaignAnalytics,
} from '../../types/campaigns/campaigns.types';
import { cn } from '../../utils/cn';
import DateRangePicker from '../ui/DateRangePicker';
import {
  Eye,
  Target,
  Zap,
  Filter,
  Trophy,
  Mail,
  HelpCircle,
  FileText,
  Calendar,
  Users,
  Clock,
  BarChart3,
  MoreHorizontal,
  MousePointer,
  MailX,
  ShieldAlert,
  Webhook,
} from 'lucide-react';
import MetricsDashboard from './MetricsDashboard';
import SentMessages from './SentMessages';
import DraftMessages from './DraftMessages';
import FailedMessages from './FailedMessages';
import TriggerConditionDisplay from './TriggerConditionDisplay';
import SparklineChart, { SparklineColorKey } from './SparklineChart';
import { selectSelectedWorkflow } from '../../store/workflows/workflows.selector';
import { selectCampaignAnalytics } from '../../store/campaigns/campaigns.selector';
import { getCampaignAnalyticsRequest } from '../../store/campaigns/campaigns.action';

type MatrixKey = keyof CampaignAnalytics['matrix'];

interface CampaignDashboardProps {
  campaign: ICampaign;
  className?: string;
  onEditWorkflow?: () => void;
  onPauseCampaign?: () => void;
  onResumeCampaign?: () => void;
}

const toDateOnlyString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLast30DaysRange = () => {
  const today = new Date();
  const endDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 29
  );
  return { startDate, endDate };
};

const CampaignDashboard: React.FC<CampaignDashboardProps> = ({
  campaign,
  className,
  onEditWorkflow,
  onPauseCampaign,
  onResumeCampaign,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [activeLeaderboardTab, setActiveLeaderboardTab] = useState('Sent');
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(() => getLast30DaysRange());
  const dispatch = useDispatch();

  // Get selected workflow from Redux store
  const selectedWorkflow = useSelector(selectSelectedWorkflow);

  // Get analytics data from Redux store
  const analytics = useSelector(selectCampaignAnalytics);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    // { id: 'metrics', label: 'Metrics', icon: Activity },
    // { id: 'ab-tests', label: 'A/B Tests', icon: FlaskConical },
    { id: 'drafts', label: 'Drafts', icon: FileText },
    { id: 'sent', label: 'Sent', icon: Mail },
    { id: 'failed', label: 'Failed', icon: MailX },
    // { id: 'journeys', label: 'Journeys', icon: Users },
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Extract trigger conditions and type from selected workflow's flow data
  const getTriggerMeta = () => {
    if (
      !selectedWorkflow ||
      !selectedWorkflow.flow ||
      !selectedWorkflow.flow.nodes
    ) {
      return {
        conditions: null as { [key: string]: any[] } | null,
        triggerType: 'attribute-segment' as 'event' | 'attribute-segment',
        eventName: '',
      };
    }

    const triggerNode = selectedWorkflow.flow.nodes.find(
      (node: any) => node.type === 'trigger'
    );
    if (!triggerNode?.data?.config) {
      return {
        conditions: null,
        triggerType: 'attribute-segment' as const,
        eventName: '',
      };
    }

    const config = triggerNode.data.config;
    return {
      conditions: config.conditions || null,
      triggerType:
        config.trigger === 'event'
          ? ('event' as const)
          : ('attribute-segment' as const),
      eventName: config.event || '',
    };
  };

  const {
    conditions: triggerConditions,
    triggerType,
    eventName: triggerEventName,
  } = getTriggerMeta();

  // Fetch analytics data when component mounts
  useEffect(() => {
    if (campaign._id) {
      if (dateRange.startDate && dateRange.endDate) {
        dispatch(
          getCampaignAnalyticsRequest(
            campaign._id,
            toDateOnlyString(dateRange.startDate),
            toDateOnlyString(dateRange.endDate)
          )
        );
        return;
      }

      const defaultRange = getLast30DaysRange();
      dispatch(
        getCampaignAnalyticsRequest(
          campaign._id,
          toDateOnlyString(defaultRange.startDate),
          toDateOnlyString(defaultRange.endDate)
        )
      );
    }
  }, [dispatch, campaign._id, dateRange.startDate, dateRange.endDate]);

  // Calculate analytics data for KPIs
  const getAnalyticsData = () => {
    if (!analytics) {
      return {
        sent: campaign.delivered || 0,
        opened: campaign.opened || 0,
        clicked: campaign.clicked || 0,
        converted: campaign.converted || 0,
        openRate: 0,
        clickRate: 0,
        conversionRate: 0,
        failed: campaign.failed || 0,
        failedRate: 0,
        complained: 0,
        complainedRate: 0,
      };
    }

    const sent = analytics.sent || campaign.delivered || 0;
    const opened = analytics.opened || campaign.opened || 0;
    const clicked = analytics.clicked || campaign.clicked || 0;
    const delivered = analytics.delivered || 0;
    const converted = analytics.converted || 0;
    const drafts = analytics.drafts || 0;
    const total = analytics.total || 0;
    const failed = analytics.failed || 0;
    const complained = analytics.complained || 0;
    const sentRate = sent > 0 ? (sent / total) * 100 : 0;
    const deliveredRate = delivered > 0 ? (delivered / total) * 100 : 0;
    const openRate = opened > 0 ? (opened / total) * 100 : 0;
    const clickRate = clicked > 0 ? (clicked / total) * 100 : 0;
    const draftsRate = drafts > 0 ? (drafts / total) * 100 : 0;
    const failedRate = failed > 0 ? (failed / total) * 100 : 0;
    const complainedRate = complained > 0 ? (complained / total) * 100 : 0;
    const conversionRate = analytics.conversionRate || 0;
    return {
      sent,
      sentRate,
      delivered,
      deliveredRate,
      opened,
      openRate,
      clicked,
      clickRate,
      converted,
      conversionRate,
      drafts,
      draftsRate,
      failed,
      failedRate,
      complained,
      complainedRate,
    };
  };

  const analyticsData = getAnalyticsData();

  // Helper functions to check if we have data for each leaderboard tab
  const hasDataForTab = (tab: string) => {
    switch (tab) {
      case 'Sent':
        return analyticsData.sent > 0;
      case 'Opened':
        return analyticsData.opened > 0;
      case 'Clicked':
        return analyticsData.clicked > 0;
      case 'Converted':
        return analyticsData.converted > 0;
      case 'Failed':
        return analyticsData.failed > 0;
      default:
        return false;
    }
  };

  // Get the appropriate data for the active leaderboard tab
  const getLeaderboardData = (tab: string) => {
    switch (tab) {
      case 'Sent':
        return {
          value: analyticsData.sent,
          percentage: analyticsData.deliveredRate,
          label: 'Most Sent',
          color: 'success',
        };
      case 'Opened':
        return {
          value: analyticsData.opened,
          percentage: analyticsData.openRate,
          label: 'Most Opened',
          color: 'info',
        };
      case 'Clicked':
        return {
          value: analyticsData.clicked,
          percentage: analyticsData.clickRate,
          label: 'Most Clicked',
          color: 'primary',
        };
      case 'Converted':
        return {
          value: analyticsData.converted,
          percentage: analyticsData.conversionRate,
          label: 'Most Converted',
          color: 'warning',
        };
      case 'Failed':
        return {
          value: analyticsData.failed,
          percentage: analyticsData.failedRate,
          label: 'Most Failed',
          color: 'danger',
        };
      default:
        return null;
    }
  };

  // Get empty state message for each tab
  const getEmptyStateMessage = (tab: string) => {
    switch (tab) {
      case 'Sent':
        return 'Send more messages to see them here';
      case 'Opened':
        return "You don't have tracked opens in this time period";
      case 'Clicked':
        return "You don't have tracked link clicks in this time period";
      case 'Converted':
        return "You don't have conversions in this time period";
      case 'Failed':
        return "You don't have failed messages in this time period";
      default:
        return 'No data available';
    }
  };

  // Get empty state icon for each tab
  const getEmptyStateIcon = (tab: string) => {
    switch (tab) {
      case 'Sent':
        return Mail;
      case 'Opened':
        return Eye;
      case 'Clicked':
        return MousePointer;
      case 'Converted':
        return Target;
      case 'Failed':
        return MailX;
      default:
        return HelpCircle;
    }
  };
  const webhookAnalytics = analytics?.webhooks;
  const webhookTotal = webhookAnalytics?.total ?? 0;

  // Real data for KPIs based on analytics. matrixKey: which matrix series to show as chart.
  const dashboardData: Array<{
    title: string;
    value: string;
    percentage: string;
    icon: typeof FileText;
    color: 'primary' | 'success' | 'warning' | 'danger';
    description: string;
    matrixKey: MatrixKey | null;
    matrixData?: Record<string, number>;
    chartTotal?: number;
    sparklineColor: SparklineColorKey;
  }> = [
    {
      title: 'DRAFTS',
      value: `${analyticsData?.drafts || 0}`,
      percentage: `${(analyticsData?.draftsRate || 0)?.toFixed(0)}%`,
      icon: FileText,
      color: 'primary',
      description: 'In draft',
      matrixKey: 'drafts',
      sparklineColor: 'primary',
    },
    {
      title: 'SENT',
      value: `${analyticsData?.sent || 0}`,
      percentage: `${(analyticsData?.sentRate || 0)?.toFixed(0)}%`,
      icon: Mail,
      color: 'primary',
      description: 'Delivered',
      matrixKey: 'sent',
      sparklineColor: 'primary',
    },
    {
      title: 'OPENED',
      value: `${analyticsData.opened || 0}`,
      percentage: `${(analyticsData.openRate || 0).toFixed(0)}%`,
      icon: Eye,
      color: 'success',
      description: 'Open rate',
      matrixKey: 'opened',
      sparklineColor: 'success',
    },
    {
      title: 'CLICKED',
      value: `${analyticsData.clicked || 0}`,
      percentage: `${(analyticsData.clickRate || 0)?.toFixed(0)}%`,
      icon: MousePointer,
      color: 'primary',
      description: 'Click rate',
      matrixKey: 'clicked',
      sparklineColor: 'primary',
    },
    {
      title: 'CONVERTED',
      value: `${analyticsData.converted || 0}`,
      percentage: `${analyticsData.conversionRate.toFixed(0)}%`,
      icon: Trophy,
      color: 'warning',
      description: 'Conversions',
      matrixKey: 'converted',
      sparklineColor: 'warning',
    },
    {
      title: 'FAILED',
      value: `${analyticsData.failed || 0}`,
      percentage: `${analyticsData.failedRate.toFixed(0)}%`,
      icon: MailX,
      color: 'danger',
      description: 'Delivery failed',
      matrixKey: 'failed',
      sparklineColor: 'destructive',
    },
    {
      title: 'COMPLAINED',
      value: `${analyticsData.complained || 0}`,
      percentage: `${(analyticsData.complainedRate || 0).toFixed(0)}%`,
      icon: ShieldAlert,
      color: 'warning',
      description: 'Complaints',
      matrixKey: 'complained',
      sparklineColor: 'warning',
    },
    ...(webhookAnalytics
      ? [
          {
            title: 'WEBHOOKS TOTAL',
            value: `${webhookAnalytics.total}`,
            percentage: '100%',
            icon: Webhook,
            color: 'primary' as const,
            description: 'Total webhook calls',
            matrixKey: null,
            matrixData: webhookAnalytics.matrix.total,
            chartTotal: webhookTotal,
            sparklineColor: 'primary' as SparklineColorKey,
          },
          {
            title: 'WEBHOOKS SENT',
            value: `${webhookAnalytics.sent}`,
            percentage: `${webhookTotal > 0 ? ((webhookAnalytics.sent / webhookTotal) * 100).toFixed(0) : 0}%`,
            icon: Webhook,
            color: 'success' as const,
            description: 'Successfully sent',
            matrixKey: null,
            matrixData: webhookAnalytics.matrix.sent,
            chartTotal: webhookTotal,
            sparklineColor: 'success' as SparklineColorKey,
          },
          {
            title: 'WEBHOOKS FAILED',
            value: `${webhookAnalytics.failed}`,
            percentage: `${webhookTotal > 0 ? ((webhookAnalytics.failed / webhookTotal) * 100).toFixed(0) : 0}%`,
            icon: Webhook,
            color: 'danger' as const,
            description: 'Delivery failed',
            matrixKey: null,
            matrixData: webhookAnalytics.matrix.failed,
            chartTotal: webhookTotal,
            sparklineColor: 'destructive' as SparklineColorKey,
          },
        ]
      : []),
  ];

  type MetricCard = (typeof dashboardData)[number];

  const renderMetricCard = (data: MetricCard, compact = false) => {
    const Icon = data.icon;
    const barColorClass =
      data.color === 'primary'
        ? 'bg-primary'
        : data.color === 'success'
          ? 'bg-success'
          : data.color === 'warning'
            ? 'bg-warning'
            : data.color === 'danger'
              ? 'bg-destructive'
              : 'bg-primary';
    const matrixData =
      data.matrixData ??
      (data.matrixKey && analytics?.matrix
        ? analytics.matrix[data.matrixKey]
        : undefined);
    const hasChartData =
      matrixData &&
      typeof matrixData === 'object' &&
      Object.keys(matrixData).length > 0;
    const showChart = Boolean(hasChartData);
    const chartTotal = data.chartTotal ?? analytics?.total ?? 1;

    return (
      <div
        key={data.title}
        className={cn(
          'group bg-card rounded-xl border border-border shadow-sm hover:shadow-md hover:border-border/80 transition-all duration-200 flex flex-col',
          compact ? 'h-full p-3.5' : 'p-5 min-h-[180px]'
        )}
      >
        <div
          className={cn(
            'flex-1 min-w-0',
            compact ? 'space-y-1.5' : 'space-y-3'
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                {data.title}
              </p>
              <h3
                className={cn(
                  'mt-1 font-bold tabular-nums text-card-foreground',
                  compact ? 'text-xl' : 'text-2xl'
                )}
              >
                {data.value}
              </h3>
            </div>
            <div className="shrink-0">
              <Icon className="w-5 h-5 text-muted-foreground" aria-hidden />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-sm truncate">
              {data.description}
            </span>
            <span className="text-foreground text-sm font-semibold tabular-nums shrink-0">
              {data.percentage}
            </span>
          </div>
        </div>
        <div
          className={cn(
            'flex items-end',
            compact ? 'mt-3 min-h-[56px]' : 'mt-4 min-h-[56px]'
          )}
        >
          {showChart && matrixData ? (
            <SparklineChart
              data={matrixData as Record<string, number>}
              color={data.sparklineColor}
              height={56}
              showAsPercentage={
                data.matrixData !== undefined ||
                [
                  'sent',
                  'opened',
                  'clicked',
                  'converted',
                  'failed',
                  'complained',
                ].includes(data.matrixKey ?? '')
              }
              total={chartTotal}
            />
          ) : (
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500 min-w-[2px]',
                  barColorClass
                )}
                style={{ width: data.percentage }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const failedCardIndex = dashboardData.findIndex(
    (data) => data.title === 'FAILED'
  );
  const cardsBeforeOutcome =
    failedCardIndex === -1
      ? dashboardData
      : dashboardData.slice(0, failedCardIndex);
  const outcomeCards =
    failedCardIndex === -1
      ? []
      : dashboardData.slice(failedCardIndex, failedCardIndex + 2);
  const cardsAfterOutcome =
    failedCardIndex === -1 ? [] : dashboardData.slice(failedCardIndex + 2);

  return (
    <div className={cn('w-full h-full bg-background', className)}>
      {/* Enhanced Header with Campaign Info */}
      <div className="bg-card border-b border-border">
        <div className="px-6 py-4">
          {/* Enhanced Navigation Tabs */}
          <div className="w-full flex items-center justify-between flex-nowrap gap-4">
            <div className="inline-flex items-center gap-1 rounded-lg bg-muted/80 px-1.5 py-1 border border-border/50">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200',
                      activeTab === tab.id
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Quick Stats - stay on one line, don't shrink */}
            <div className="flex items-center shrink-0 gap-4 text-sm text-muted-foreground whitespace-nowrap">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{campaign.recipientsCount || 0} subscribers</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Started {formatDate(campaign.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters - Only show for Overview tab */}
      {activeTab === 'overview' && (
        <div className="px-6 pt-4">
          <div className="flex items-center">
            <div className="flex items-center space-x-3">
              <DateRangePicker
                value={dateRange}
                onChange={(dateRange) => {
                  if (dateRange.startDate && dateRange.endDate) {
                    setDateRange(dateRange);
                    return;
                  }
                  setDateRange(getLast30DaysRange());
                }}
                placeholder="Select date range"
                className="min-w-[280px]"
              />
            </div>
          </div>
        </div>
      )}

      {/* Conditional Content Based on Active Tab */}
      {activeTab === 'metrics' ? (
        <MetricsDashboard />
      ) : activeTab === 'drafts' ? (
        <DraftMessages campaignId={campaign._id} campaignName={campaign.name} />
      ) : activeTab === 'sent' ? (
        <SentMessages campaignId={campaign._id} campaignName={campaign.name} />
      ) : activeTab === 'failed' ? (
        <FailedMessages
          campaignId={campaign._id}
          campaignName={campaign.name}
        />
      ) : activeTab === 'overview' ? (
        <div className="flex-1 p-6 space-y-6">
          {/* Enhanced KPI Cards – charts where matrix data exists, progress bar for converted */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cardsBeforeOutcome.map((data) => renderMetricCard(data))}
            {outcomeCards.length > 0 && (
              <div className="grid grid-cols-2 gap-3 h-full min-h-[180px]">
                {outcomeCards.map((data) => renderMetricCard(data, true))}
              </div>
            )}
            {cardsAfterOutcome.map((data) => renderMetricCard(data))}
          </div>

          {/* Enhanced Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left Panel - Campaign Details and Configuration */}
            <div className="space-y-4">
              {/* Enhanced Details Section */}
              <div className="bg-card rounded-xl border border-border p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-card-foreground">
                    Campaign Details
                  </h3>
                  <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors">
                    Edit Details
                  </button>
                </div>

                <div className="space-y-0">
                  {/* Detail row: Created & Started */}
                  <div className="flex items-start gap-3 py-3 border-b border-border">
                    <div className="w-6 h-6 shrink-0 bg-primary/10 rounded-md flex items-center justify-center mt-0.5">
                      <Calendar className="w-3 h-3 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-card-foreground">
                        Created & Started
                      </p>
                      <div className="mt-1 space-y-0.5">
                        <p className="text-xs text-muted-foreground">
                          Created {formatDate(campaign.createdAt)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Last updated {formatDate(campaign.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Detail row: Trigger */}
                  <div className="py-3 border-b border-border">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-6 h-6 shrink-0 bg-primary/10 rounded-md flex items-center justify-center">
                          <Zap className="w-3 h-3 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-card-foreground">
                            Trigger
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Entry conditions
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-primary hover:text-primary-hover text-xs font-medium transition-colors shrink-0"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 border border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">
                        {triggerType === 'event'
                          ? 'People will enter this campaign every time they perform the event:'
                          : 'People will enter this campaign the first time they meet the following conditions:'}
                      </p>
                      {triggerConditions || triggerType === 'event' ? (
                        <TriggerConditionDisplay
                          conditions={triggerConditions || {}}
                          triggerType={triggerType}
                          eventName={triggerEventName}
                        />
                      ) : (
                        <div className="text-center text-muted-foreground py-3">
                          <p className="text-xs font-medium">
                            No trigger conditions configured
                          </p>
                          <p className="text-xs text-muted-foreground/70 mt-1">
                            Edit the workflow to set up trigger conditions
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Settings */}
                  {/* <div className="py-2 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-gray-50 rounded-md flex items-center justify-center">
                          <Settings className="w-3 h-3 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">
                            Settings
                          </p>
                          <p className="text-xs text-gray-500">
                            Campaign configuration
                          </p>
                        </div>
                      </div>
                      <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors">
                        Edit
                      </button>
                    </div>
                    <div className="space-y-1 text-xs text-gray-600">
                      <p>Messages will be sent to: All subscribed people</p>
                      <p>Message limit is ON</p>
                    </div>
                  </div> */}

                  {/* Enhanced Goal */}
                  {/* <div className="py-2 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-orange-50 rounded-md flex items-center justify-center">
                          <Target className="w-3 h-3 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">
                            Goal
                          </p>
                          <p className="text-xs text-gray-500">
                            Campaign objective
                          </p>
                        </div>
                      </div>
                      <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors">
                        Edit
                      </button>
                    </div>
                    <p className="text-xs text-gray-600">None</p>
                  </div> */}

                  {/* Enhanced Exit */}
                  {/* <div className="py-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-destructive-background rounded-md flex items-center justify-center">
                          <LogOut className="w-3 h-3 text-destructive" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">
                            Exit
                          </p>
                          <p className="text-xs text-gray-500">
                            Exit conditions
                          </p>
                        </div>
                      </div>
                      <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors">
                        Edit
                      </button>
                    </div>
                    <p className="text-xs text-gray-600">
                      People will exit the campaign when they complete the
                      workflow.
                    </p>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Right Panel - Enhanced Message Leaderboard */}
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-card-foreground">
                    Message Leaderboard
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Performance by message
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-muted-foreground">
                    {dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} to ${dateRange.endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : 'Select date range'}
                  </span>
                  <div className="flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-full">
                    <span className="text-xs font-medium text-primary">
                      5 channels
                    </span>
                  </div>
                </div>
              </div>

              {/* Enhanced Leaderboard Tabs */}
              <div className="inline-flex items-center gap-1 rounded-lg bg-muted/80 px-1.5 py-1 border border-border/50 mb-4">
                {['Sent', 'Opened', 'Clicked'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveLeaderboardTab(tab)}
                    className={cn(
                      'flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200',
                      tab === activeLeaderboardTab
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                    )}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Enhanced Sort/Filter Option */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-1">
                  <Filter className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">
                    Most {activeLeaderboardTab.toLowerCase()}
                  </span>
                </div>
                <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <MoreHorizontal className="w-3 h-3" />
                </button>
              </div>

              {/* Enhanced Leaderboard Entries */}
              <div className="space-y-2">
                {hasDataForTab(activeLeaderboardTab)
                  ? // Data available - show leaderboard entry
                    (() => {
                      const data = getLeaderboardData(activeLeaderboardTab);
                      if (!data) return null;

                      const containerClasses = {
                        success: 'bg-success-background border border-success',
                        info: 'bg-info-background border border-info',
                        primary: 'bg-primary/10 border border-primary',
                        warning: 'bg-warning-background border border-warning',
                      };

                      const badgeClasses = {
                        success: 'bg-success text-success-foreground',
                        info: 'bg-info text-info-foreground',
                        primary: 'bg-primary text-primary-foreground',
                        warning: 'bg-warning text-warning-foreground',
                      };

                      const iconLabelClasses = {
                        success: 'text-success',
                        info: 'text-info',
                        primary: 'text-primary',
                        warning: 'text-warning',
                      };

                      const colorKey =
                        data.color as keyof typeof containerClasses;

                      return (
                        <div
                          className={`rounded-lg p-3 ${containerClasses[colorKey]}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center ${badgeClasses[colorKey]}`}
                              >
                                <span className="text-xs font-bold">1</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Trophy
                                  className={`w-3 h-3 ${iconLabelClasses[colorKey]}`}
                                />
                                <span
                                  className={`text-xs font-semibold ${iconLabelClasses[colorKey]} uppercase tracking-wide`}
                                >
                                  {data.label}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center space-x-1">
                                <Mail className="w-3 h-3 text-muted-foreground" />
                                <span className="text-xs font-medium text-card-foreground">
                                  Email 1
                                </span>
                              </div>
                              <div className="bg-card px-2 py-1 rounded-full">
                                <span className="text-xs font-bold text-card-foreground">
                                  {data.value}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  : // No data - show empty state
                    (() => {
                      const EmptyIcon = getEmptyStateIcon(activeLeaderboardTab);
                      const message =
                        getEmptyStateMessage(activeLeaderboardTab);

                      return (
                        <div className="text-center py-8">
                          <div className="flex justify-center mb-3">
                            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                              <EmptyIcon className="w-6 h-6 text-muted-foreground" />
                            </div>
                          </div>
                          <h4 className="text-sm font-medium text-card-foreground mb-1">
                            {message}
                          </h4>
                          {activeLeaderboardTab === 'Sent' && (
                            <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors mt-2">
                              Create new message
                            </button>
                          )}
                          {activeLeaderboardTab === 'Clicked' && (
                            <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors mt-2">
                              Learn more about tracking links
                            </button>
                          )}
                        </div>
                      );
                    })()}

                {/* Entry 2 - Placeholder (only show if we have data) */}
                {hasDataForTab(activeLeaderboardTab) && (
                  <div className="border-2 border-dashed border-border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-muted-foreground">
                            2
                          </span>
                        </div>
                      </div>
                      <div className="text-center flex-1">
                        <p className="text-xs text-muted-foreground mb-1">
                          Send more messages to see them here
                        </p>
                        <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors">
                          Create new message
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Active Tests Section */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Active Tests
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  A/B testing performance
                </p>
              </div>
              <button className="text-primary hover:text-primary-hover text-xs font-medium transition-colors">
                View Details
              </button>
            </div> */}

          {/* Enhanced Tests Table */}
          {/* <div className="overflow-hidden rounded-md border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-900">
                      TEST
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-900">
                      PERCENTAGES
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-900">
                      DELIVERED
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-900">
                      OPENED
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-900">
                      CLICKED
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-900">
                      CONVERTED
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={6} className="py-8">
                      <div className="text-center">
                        <div className="flex justify-center mb-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                            <FlaskConical className="w-6 h-6 text-gray-400" />
                          </div>
                        </div>
                        <h4 className="text-base font-medium text-gray-900 mb-1">
                          No active tests
                        </h4>
                        <p className="text-xs text-gray-500 mb-3">
                          You do not have any active tests running
                        </p>
                        <button className="inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary-background rounded-md hover:bg-primary-muted transition-colors">
                          <FlaskConical className="w-3 h-3" />
                          <span>Learn more about A/B Testing</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div> */}
        </div>
      ) : (
        <div className="flex-1 p-6">
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Content
              </h3>
              <p className="text-muted-foreground">
                Content for {activeTab} tab will be implemented here.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDashboard;
